import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import NeuralCanvas from "@/components/NeuralCanvas";
import { PlazasVIP, useOffer } from "@/components/Offer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackPurchase, trackLead } from "@/lib/analytics";

function eur(n) { return `${new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(Number(n))} €`; }

export default function Checkout() {
  const navigate = useNavigate();
  const { user, refreshUser, setSession } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const tipo = params.get("tipo") || "vip";
  const slug = params.get("slug");

  const { data: offer } = useOffer();

  const [procesando, setProcesando] = useState(false);
  const [guest, setGuest] = useState({ nombre: "", email: "" });
  const guestOk = !!user || (guest.nombre.trim().length > 1 && guest.email.includes("@"));

  // ---- return / polling mode ----
  const [pollState, setPollState] = useState(sessionId ? "checking" : null);
  const [pollInfo, setPollInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setPollInfo(data);
        if (data.payment_status === "paid") {
          setPollState("paid");
          try { trackPurchase({ value: Number(data.amount_total || data.amount || 2900), currency: (data.currency || "eur").toUpperCase() }); } catch { /* noop */ }
          if (data.token && data.user) setSession({ token: data.token, user: data.user });
          else await refreshUser();
          return;
        }
        if (data.status === "expired") { setPollState("expired"); return; }
      } catch (e) { /* keep trying */ }
      attempts += 1;
      if (attempts >= 8) { setPollState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refreshUser, setSession]);

  const esVip = true;
  const vipPrice = offer?.precio_actual ?? 2900;
  const precioLabel = eur(vipPrice);

  const pagar = async () => {
    if (procesando || !guestOk) return;
    setProcesando(true);
    try {
      const payload = { tipo, slug: slug || undefined, origin_url: window.location.origin };
      if (!user) { payload.nombre = guest.nombre; payload.email = guest.email; }
      const { data } = await api.post("/payments/checkout", payload);
      try { trackLead(); } catch { /* noop */ }
      window.location.href = data.url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "No se pudo iniciar el pago. Inténtalo de nuevo.");
      setProcesando(false);
    }
  };

  // ---- success / error screens ----
  if (sessionId) {
    if (pollState === "paid") {
      return (
        <div className="lci"><div className="checkout-wrap">
          <NeuralCanvas />
          <div className="co-ok rise" data-testid="checkout-success">
            <div className="big">✓</div>
            <h2>¡Pago completado!</h2>
            <p>
              Bienvenido/a a Liberti Club IA. Te hemos enviado el recibo y tus credenciales por email.
              {esVip ? " Tu plaza vitalicia queda reservada para siempre." : " Tu acceso al programa ya está activo."}
            </p>
            <button className="btn btn-gold" data-testid="ir-campus" onClick={() => navigate("/campus")}>Entrar al campus →</button>
          </div>
        </div></div>
      );
    }
    return (
      <div className="lci"><div className="checkout-wrap">
        <NeuralCanvas />
        <div className="co-ok rise" data-testid="checkout-processing">
          <div className="big">{pollState === "expired" || pollState === "timeout" ? "⏳" : "💳"}</div>
          <h2>{pollState === "checking" ? "Verificando tu pago…" : pollState === "expired" ? "Sesión de pago expirada" : "No hemos podido confirmar el pago"}</h2>
          <p>{pollState === "checking" ? "Un momento, estamos confirmando tu pago con Stripe." : "Si el importe se cobró, tu acceso se activará automáticamente. Puedes reintentar el pago."}</p>
          <button className="btn btn-gold" onClick={() => navigate(`/checkout?tipo=${tipo}${slug ? `&slug=${slug}` : ""}`)}>Reintentar →</button>
        </div>
      </div></div>
    );
  }

  return (
    <div className="lci"><div className="checkout-wrap">
      <NeuralCanvas />
      <div style={{ position: "relative", zIndex: 2, maxWidth: 960, margin: "0 auto 22px" }}>
        <button className="back" onClick={() => navigate(-1)}>← Volver</button>
      </div>
      <div className="checkout">
        <div className="co-panel">
          <h2>Finalizar compra</h2>
          <p className="hint">Pago 100% seguro. Procesado por Stripe.</p>

          {esVip ? (
            <>
              <div style={{ border: "1px solid rgba(47,107,255,.35)", background: "rgba(47,107,255,.06)", padding: "16px 18px", marginBottom: 18 }}>
                <div style={{ fontSize: 12, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--gold)", fontWeight: 600, marginBottom: 10 }}>Elige cómo pagar</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {["💳 Tarjeta", "Klarna · paga en 3", "PayPal", " Pay / G Pay"].map((m) => (
                    <span key={m} className="chip" style={{ textTransform: "none", letterSpacing: 0 }}>{m}</span>
                  ))}
                </div>
                <p style={{ color: "var(--muted)", fontSize: 12.5, fontWeight: 300, lineHeight: 1.6, marginTop: 12 }}>
                  Con <b style={{ color: "var(--cream)" }}>Klarna</b> puedes dividir los 2.900 € en <b style={{ color: "var(--cream)" }}>3 plazos sin intereses</b> —
                  nosotros recibimos el importe completo al instante. Elegirás el método en la pasarela segura de Stripe.
                </p>
              </div>
            </>
          ) : (
            <p style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.7, fontWeight: 300, marginBottom: 20 }}>
              Te llevaremos a la pasarela segura de <b style={{ color: "var(--cream)" }}>Stripe</b> para pagar con tarjeta.
              Renovación mensual · cancela cuando quieras.
            </p>
          )}

          {!user && (
            <div style={{ marginBottom: 18 }} data-testid="guest-fields">
              <div style={{ fontSize: 12, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--gold)", fontWeight: 600, marginBottom: 12 }}>Tus datos</div>
              <div className="field" style={{ marginBottom: 12 }}>
                <label htmlFor="g-nombre">Nombre</label>
                <input id="g-nombre" data-testid="guest-nombre" value={guest.nombre} onChange={(e) => setGuest({ ...guest, nombre: e.target.value })} placeholder="Tu nombre" />
              </div>
              <div className="field" style={{ marginBottom: 4 }}>
                <label htmlFor="g-email">Email</label>
                <input id="g-email" data-testid="guest-email" type="email" value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} placeholder="tu@email.com" />
              </div>
              <p style={{ color: "var(--muted)", fontSize: 11.5, fontWeight: 300, lineHeight: 1.5 }}>
                Crearemos tu acceso al campus con este email y te enviaremos tus credenciales tras el pago.
              </p>
            </div>
          )}

          <button className="btn btn-gold" data-testid="pagar-btn" style={{ width: "100%", justifyContent: "center", opacity: guestOk ? 1 : 0.45 }} onClick={pagar}>
            {procesando ? "Conectando con Stripe…" : `Pagar ${precioLabel} →`}
          </button>

          <div className="co-secure">
            <span>🔒 <b>SSL</b> cifrado</span><span>◆</span><span>Powered by <b>Stripe</b></span><span>◆</span><span>Garantía 14 días</span>
          </div>
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <button className="link-gold" onClick={() => navigate("/agendar")}>¿Dudas antes de pagar? Agenda una llamada gratuita →</button>
          </div>
          <p style={{ textAlign: "center", color: "var(--muted)", fontSize: 11, fontWeight: 300, marginTop: 14, lineHeight: 1.6 }}>
            Al pagar aceptas las <button className="link-gold" style={{ fontSize: 11 }} onClick={() => navigate("/legal/condiciones")}>condiciones de contratación</button> y el
            derecho de desistimiento de 14 días.
          </p>
        </div>

        <div className="co-panel">
          <h2 className="serif" style={{ fontSize: 20 }}>Tu pedido</h2>
          <div className="co-item">
            <span className="n">Máster IA · Acceso vitalicio</span>
            <span className="p">{precioLabel}</span>
          </div>
          <div className="co-total">
            <span>Total (pago único)</span>
            <span className="p">{eur(vipPrice)}</span>
          </div>
          <div style={{ marginTop: 20 }}><PlazasVIP /></div>
          <ul className="vip-list" style={{ marginTop: 18 }}>
            <li>Acceso vitalicio al Máster completo</li>
            <li>Mentorías semanales en directo, para siempre</li>
            <li>Comunidad VIP privada de España</li>
          </ul>
        </div>
      </div>
    </div></div>
  );
}
